import { useCallback, useState } from 'react';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import { RecurringSubscriptionStatus, SubscriptionOrderStatus } from 'chaika-enums';
import { EhrChatData, RecurringSubscriptionData, SubscriptionOrderData } from 'chaika-frontend-types/frontend-cms';

import pupupStyle from '../ChatCreationPopup.module.scss';

import { translate } from '@chaika/i18n/helper';

import { EhrApi } from '@api/EhrApi';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { useUserPractice } from '@user/hooks/useUserPractice';
import { userQueryOptions } from '@user/queries/userQueries';
import { getFullName } from '@user/utils/getFullName';
import { isDefined } from '@utils/isDefined';
import { isActivatedSubscriptionOrder } from '@patient/utils/isActivatedSubscriptionOrder';

import { ChatCreationPopup } from '../ChatCreationPopup';
import { ChatPreloader } from '../ChatPreloader';
import { usePatientChats } from './usePatientChats';
import { useSubscriptions } from './useSubscriptions';

interface Params {
    patientId: string;
    fromEntry?: boolean;
}

export interface UseChatCreation {
    chats: EhrChatData[];
    doPatientHasChats: boolean;
    isChatsLoaded: boolean;
    subscriptionOrders: SubscriptionOrderData[];
    recurringSubscriptions: RecurringSubscriptionData[];
    hasActiveSubscription: boolean;
    hasTrialSubscription: boolean;
    isSent: boolean;
    isUserBelongToPractice(practiceId: string): boolean;
    onChatCreation(doctorId?: string, isPracticeChat?: boolean): Promise<void>;
}

export function useChatCreation({ patientId, fromEntry }: Params): UseChatCreation {
    const { app, authUser } = store;

    const { isUserBelongToPractice, practiceWithTgLint } = useUserPractice({ userId: authUser.id });
    const { subscriptionOrders, recurringSubscriptions, reload: reloadSubscriptions } = useSubscriptions({ patientId });
    const { chats, doPatientHasChats, isLoaded: isChatsLoaded, reload: reloadChats } = usePatientChats({ patientId });

    const hasActiveSubscription =
        subscriptionOrders.some(isActivatedSubscriptionOrder) ||
        recurringSubscriptions.some(subscription => subscription.status === RecurringSubscriptionStatus.Active);

    const hasTrialSubscription =
        subscriptionOrders.some(order => order.status === SubscriptionOrderStatus.Active && isNil(order.paymentId)) ||
        recurringSubscriptions.some(
            subscription => subscription.status === RecurringSubscriptionStatus.Active && subscription.isTrial,
        );

    const [isSent, setIsSent] = useState(false);
    const [title, setTitle] = useState(translate('createChat'));

    const onChatCreated = useCallback(async () => {
        setIsSent(true);

        await reloadSubscriptions();

        setTimeout(() => {
            setIsSent(false);
        }, 5000);
    }, [reloadSubscriptions]);

    const onChatCreation = useCallback(
        async (doctorId?: string, isPracticeChat?: boolean) => {
            if (!authUser.organizationId) {
                app.setSelectOrganizationPopupParams({
                    onSaveClick: () => onChatCreation(doctorId, isPracticeChat),
                });

                return;
            }

            const doPatientHasSubscriptions = hasActiveSubscription || hasTrialSubscription;

            if (isDefined(doctorId) && doPatientHasSubscriptions) {
                const subscriptionDoctorId =
                    subscriptionOrders
                        .filter(isActivatedSubscriptionOrder)
                        .map(so => so.doctorId)
                        .find(isDefined) ??
                    recurringSubscriptions
                        .filter(rs => rs.status === RecurringSubscriptionStatus.Active)
                        .map(rs => rs.doctorId)
                        .find(isDefined);

                const subscriptionDoctor = isDefined(subscriptionDoctorId)
                    ? (
                          await queryClient
                              .fetchQuery(userQueryOptions.list({ ids: [subscriptionDoctorId] }))
                              .catch(() => [])
                      ).find(({ id }) => id === subscriptionDoctorId)
                    : undefined;
                const subscriptionDoctorName = subscriptionDoctor && getFullName(subscriptionDoctor);
                const descriptionText = subscriptionDoctorName
                    ? translate('$patientAlreadyHasDoctorSubscriptionConfirm', {
                          $doctorName: subscriptionDoctorName,
                      })
                    : translate('patientAlreadyHasDoctorSubscriptionConfirmAnonymous');
                const [firstLine, secondLine] = descriptionText.split('\n');
                const closeConfirmPopup = (): void => app.showConfirmPopup(null);

                app.showConfirmPopup({
                    description: () => (
                        <>
                            {firstLine}
                            <br />
                            {secondLine}
                        </>
                    ),
                    confirmText: translate('confirm'),
                    cancelText: translate('cancel'),
                    onConfirm: async () => {
                        closeConfirmPopup();
                        app.showPopup({
                            withoutCloseButton: true,
                            description: () => (
                                <div className={pupupStyle.popupRoot}>
                                    <div className={classnames(['font_h1', pupupStyle.title])}>{title}</div>
                                    <ChatPreloader
                                        startLoader
                                        hideSubscriptionCost
                                        doctorId={doctorId}
                                        updateTitle={setTitle}
                                        reloadChats={reloadChats}
                                    >
                                        <div />
                                    </ChatPreloader>
                                </div>
                            ),
                            onClose: app.showPopup.bind(app, null),
                        });
                        await EhrApi.createChatWithDoctor(patientId, doctorId);
                    },
                    onCancel: closeConfirmPopup,
                    onClose: closeConfirmPopup,
                });
            } else if (isPracticeChat && doPatientHasChats && practiceWithTgLint[0]) {
                const [practice] = practiceWithTgLint;

                app.showPopup({
                    withoutCloseButton: true,
                    description: () => (
                        <div className={pupupStyle.popupRoot}>
                            <div className={classnames(['font_h1', pupupStyle.title])}>{title}</div>
                            <ChatPreloader
                                startLoader
                                hideSubscriptionCost
                                practiceId={practice.id}
                                updateTitle={setTitle}
                                reloadChats={reloadChats}
                            >
                                <div />
                            </ChatPreloader>
                        </div>
                    ),
                    onClose: app.showPopup.bind(app, null),
                });
                await EhrApi.createChatWithPractice(patientId, practice.id);
            } else {
                app.showPopup({
                    withoutCloseButton: true,
                    description: () => (
                        <ChatCreationPopup
                            isPracticeChat={isPracticeChat ?? false}
                            fromEntry={fromEntry ?? false}
                            doPatientHasChats={doPatientHasChats}
                            patientId={patientId}
                            doctorId={doctorId}
                            onChatCreated={onChatCreated}
                            reloadChats={reloadChats}
                        />
                    ),
                    onClose: app.showPopup.bind(app, null),
                });
            }
        },
        [
            app,
            patientId,
            authUser.organizationId,
            fromEntry,
            doPatientHasChats,
            title,
            practiceWithTgLint,
            subscriptionOrders,
            recurringSubscriptions,
            onChatCreated,
            reloadChats,
            hasActiveSubscription,
            hasTrialSubscription,
        ],
    );

    return {
        chats,
        doPatientHasChats,
        isChatsLoaded,
        subscriptionOrders,
        recurringSubscriptions,
        hasActiveSubscription,
        hasTrialSubscription,
        isSent,
        isUserBelongToPractice,
        onChatCreation,
    };
}
